import { Anchor, Highlight, Text } from "@mantine/core";
import { useLocation } from "wouter";

/**
 * The employer name in a contact's headline, linking to that company's page when we know which
 * Company record it is. One component for every surface that prints "Role · Company", so the
 * gesture and the fallback behave identically in search results, the contact list, the hover
 * card, the profile header and the assistant's reference rows.
 *
 * The id comes from `candidateRoleAndCompany`, which only returns one when it belongs to the
 * company being named — so this renders a link exactly when the link is correct, and plain text
 * otherwise (import-only contacts, unresolved employers). Callers do not decide.
 *
 * Two details that are load-bearing rather than cosmetic:
 *
 *  - `stopPropagation`. Every caller sits inside a row or card whose own click opens the
 *    contact. Without this, clicking the company would navigate to the person instead.
 *  - `highlight`. On the search page the matched substring is highlighted, and the company is
 *    often the field that carries the match ("Ed. Züblin"). Passing the query terms keeps that
 *    highlight inside the link, so making the name clickable does not cost the explanation of
 *    why the row was found.
 *
 * Rendered inline with `fz="inherit"`, so it never changes the size of the line it sits in.
 */
export default function CompanyLink({ company, companyId, highlight, ...props }) {
  const [, setLocation] = useLocation();
  if (!company) return null;

  const label = highlight?.length ? (
    <Highlight component="span" highlight={highlight} inherit>
      {company}
    </Highlight>
  ) : (
    company
  );

  if (!companyId) {
    return (
      <Text component="span" fz="inherit" c="inherit" {...props}>
        {label}
      </Text>
    );
  }

  return (
    <Anchor
      component="button"
      type="button"
      fz="inherit"
      style={{ display: "inline", textAlign: "left", verticalAlign: "baseline" }}
      onClick={(e) => {
        e.stopPropagation();
        setLocation(`/companies/${companyId}`);
      }}
      {...props}
    >
      {label}
    </Anchor>
  );
}
